/**
 * PromotionalManagement.jsx — 홍보물 관리 (어셈블리)
 *
 * 분할 구조:
 *   promotionalmanagement/styles.js              — CSS-in-JS 스타일 상수
 *   promotionalmanagement/utils.js               — PROMO_HEADERS, getKstToday, fmtPrice, emptyOrderForm, Field
 *   promotionalmanagement/ItemManageModal.jsx     — 홍보물 금액 관리 모달 + DropIndicator
 *   promotionalmanagement/OrderItemComponents.jsx — WorkflowStepBadge, OrderedCheckbox, CompletedCheckbox
 *   promotionalmanagement/ItemSearchInput.jsx     — 홍보물 검색 자동완성
 *   promotionalmanagement/OrderItemsList.jsx      — 주문 모달의 홍보물 명세 섹션
 *   promotionalmanagement/OrderModal.jsx          — 주문 등록/수정 모달
 *   promotionalmanagement/usePromotionalManagement.js — 페이지 상태 + 핸들러 훅
 *   promotionalmanagement/PromotionalTable.jsx    — 데스크탑 주문 테이블
 *   promotionalmanagement/OrderCard.jsx           — 모바일 주문 카드
 *   promotionalmanagement/HistoryModal.jsx        — 이력 모달
 *   promotionalmanagement/promoApi.js             — 화면이 부르는 주소 묶음(본사·회사별·통합, 2026-10-19)
 */
import React, { useEffect, useMemo, useState } from 'react';
import Pagination from '../../components/ui/Pagination';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../hooks/useOverlayClickClose';
import { ColorSwatchFilter } from '../../components/ColorSwatchFilter';
import { usePromotionalManagement } from './promotionalmanagement/usePromotionalManagement';
import { resolvePromoRowState, PROMO_COLOR_FILTERS } from './promotionalmanagement/promoRowState';
import { ItemManageModal }          from './promotionalmanagement/ItemManageModal';
import { OrderModal }               from './promotionalmanagement/OrderModal';
import { PromotionalTable }         from './promotionalmanagement/PromotionalTable';
import { OrderCard }                from './promotionalmanagement/OrderCard';
import { HistoryModal }             from './promotionalmanagement/HistoryModal';
import { HQ_PROMO_API, PromoApiContext, usePromoApi } from './promotionalmanagement/promoApi';
import { PromoScreenContext } from './promotionalmanagement/PromoCompanyAssign';
import { useAppPermissions } from '../../hooks/useAppPermissions';
import {
  itemManageBtnStyle, newOrderBtnStyle,
  cardStyle, cardHeaderStyle, cardTitleStyle, emptyStyle,
  overlayStyle, confirmBoxStyle, confirmHeaderStyle, confirmBodyStyle,
  modalHeaderIconBox, modalHeaderTitleStyle, closeBtnStyle,
  confirmFooterStyle, cancelBtnStyle, deleteConfirmBtnStyle,
} from './promotionalmanagement/styles';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, stdSearchWrap, stdSearchInput, stdSearchIcon, stdSearchPcBox, stdOutlineBtn,
} from './adminlayout/designStandard';
import { Search } from 'lucide-react';

// 주문 목록 한 페이지 건수 (2026-10-23 사용자: "홍보물 관리도 페이지 나눠줘" — 10건, 계약관리와 같음)
const PROMO_ORDERS_PER_PAGE = 10;

// api: 주소 묶음(promoApi.js) — 기본은 본사. 회사별·통합 화면은 그 화면 주소 묶음을 넘긴다.
export default function PromotionalManagement({ api = HQ_PROMO_API }) {
  return (
    <PromoApiContext.Provider value={api}>
      <PromotionalManagementBody />
    </PromoApiContext.Provider>
  );
}

function PromotionalManagementBody() {
  const api = usePromoApi();
  const {
    isMobile,
    orders, filteredOrders, loadingOrders,
    promotionalItemsList, categoriesList,
    itemManageOpen, setItemManageOpen,
    orderModalOpen, setOrderModalOpen,
    editTarget,
    deleteTarget, setDeleteTarget, deleting, deleteConfirm, setDeleteConfirm,
    historyOrder, setHistoryOrder, historyData, historyLoading,
    searchQuery, setSearchQuery,
    shippingEdit, setShippingEdit,
    fetchOrders, fetchActiveItems,
    handleItemStepChange, handleItemOrderToggle, handleItemCancel,
    openHistory, saveShippingEdit,
    openNew, openEdit, askDelete, handleDelete,
    screenMeta,
  } = usePromotionalManagement(api);
  // 2026-10-19(5-6): '금액 관리'(품목·원가)는 본사 화면 + 원가 열쇠일 때만(서버도 원가 열쇠만 — 5-3).
  //   [회사 정하기]는 통합 화면 + 서버가 허락 + 운영자·홍보물 관리 담당자(서버가 다시 본다).
  const { me, permCodes } = useAppPermissions();
  const isAdmin = me?.role === 'admin';
  const showItemManage = api.canManageItems && (isAdmin || Boolean(permCodes?.has?.('PROMOTIONAL_COST_MANAGEMENT')));
  const screenCtx = useMemo(() => ({
    companies: screenMeta?.codes || [],
    canAssign: Boolean(api.canAssignCompany && screenMeta?.canAssign && (isAdmin || permCodes?.has?.('PROMOTIONAL_MANAGEMENT'))),
    onAssigned: fetchOrders,
  }), [screenMeta, api.canAssignCompany, isAdmin, permCodes, fetchOrders]);
  useBodyScrollLock(Boolean(deleteConfirm));
  const overlayCloseProps = useOverlayClickClose(() => { setDeleteConfirm(false); setDeleteTarget(null); });

  // 배경색(상태) 필터 — 빈 Set = 전부 표시. 색을 켜면 그 상태 행만(다중 선택). 검색 위에 얹음.
  const [activeColors, setActiveColors] = useState(() => new Set());
  const toggleColor = (key) => setActiveColors((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const visibleOrders = useMemo(
    () => (activeColors.size === 0 ? filteredOrders : filteredOrders.filter((o) => activeColors.has(resolvePromoRowState(o)))),
    [filteredOrders, activeColors],
  );
  // 페이지 나누기 — 받은 목록을 화면에서 자른다(계약관리·일일콜과 같은 방식). 검색·색 거르기를 바꾸면 1페이지,
  //   목록이 줄어 지금 페이지가 사라지면 마지막 페이지로. 수정·단계 변경은 보던 페이지에 머문다.
  const [promoPage, setPromoPage] = useState(1);
  const promoTotalPages = Math.max(1, Math.ceil(visibleOrders.length / PROMO_ORDERS_PER_PAGE));
  useEffect(() => { setPromoPage(1); }, [searchQuery, activeColors]);
  useEffect(() => { setPromoPage((p) => Math.min(p, promoTotalPages)); }, [promoTotalPages]);
  const pagedOrders = useMemo(
    () => visibleOrders.slice((promoPage - 1) * PROMO_ORDERS_PER_PAGE, promoPage * PROMO_ORDERS_PER_PAGE),
    [visibleOrders, promoPage],
  );

  return (
    <PromoScreenContext.Provider value={screenCtx}>
    <div>
      {/* 페이지 헤더 — 직원 관리 표준: 텍스트 제목/부제 + 검정/외곽선 버튼 (designStandard) */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>홍보물 관리</h2>
          <div style={stdPageSubtitle}>홍보물 주문을 관리합니다.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {showItemManage && <button style={itemManageBtnStyle} onClick={() => setItemManageOpen(true)}>홍보물 금액 관리</button>}
          <button style={newOrderBtnStyle}   onClick={openNew}>+ 신규 등록</button>
        </div>
      </div>

      {/* 2줄 = 검색(제목 아래) — 출퇴근 관리 윗부분과 같은 앱 표준 검색칸(2026-10-23 사용자: "출퇴근 관리 상단 레이아웃처럼, 검색은 제목 아래").
          아래 간격은 출퇴근(40px)과 같게 보이도록 주문 목록 상자 윗여백(16px)만큼 뺀 24px. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
        <div style={isMobile ? { ...stdSearchWrap(isMobile), marginBottom: 0, flex: 1 } : stdSearchPcBox}>
          <Search size={15} style={stdSearchIcon} />
          <input
            style={stdSearchInput}
            placeholder="상호·담당자·홍보물명 검색"
            aria-label="상호·담당자·홍보물명 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        {/* 검색어가 있을 때만 '초기화'(2026-10-29 사용자: "계약관리처럼 초기화 버튼") — 검색어만 지운다(색 거르기는 그대로) */}
        {searchQuery && <button style={{ ...stdOutlineBtn, flexShrink: 0 }} onClick={() => setSearchQuery('')}>초기화</button>}
      </div>

      {/* 주문 목록 */}
      <div style={cardStyle}>
        {/* 3줄 = 목록 제목·색 거르기·건수 — 전부 오른쪽(출퇴근 관리의 색 안내 칩 자리, 사용자 선택 a). 휴대폰은 왼쪽부터 쌓는다(출퇴근과 같음). */}
        <div style={{ ...cardHeaderStyle, justifyContent: isMobile ? 'flex-start' : 'flex-end', flexWrap: 'wrap', gap: 10 }}>
          <span style={cardTitleStyle}>홍보물 주문 목록</span>
          <ColorSwatchFilter filters={PROMO_COLOR_FILTERS} active={activeColors} onToggle={toggleColor} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {(searchQuery || activeColors.size) ? `${visibleOrders.length} / ${orders.length}건` : `${orders.length}건`}
          </span>
        </div>

        {loadingOrders
          ? <div style={emptyStyle}>불러오는 중...</div>
          : visibleOrders.length === 0
            ? <div style={emptyStyle}>{(searchQuery || activeColors.size) ? '조건에 맞는 주문이 없습니다.' : '등록된 홍보물 주문이 없습니다.'}</div>
            : isMobile
              ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {pagedOrders.map((o) => (
                    <OrderCard
                      key={o.id}
                      order={o}
                      onEdit={openEdit}
                      onDelete={askDelete}
                      onItemStepChange={handleItemStepChange}
                      onItemStepToggle={handleItemOrderToggle}
                    />
                  ))}
                </div>
              )
              : (
                <PromotionalTable
                  filteredOrders={pagedOrders}
                  shippingEdit={shippingEdit}
                  setShippingEdit={setShippingEdit}
                  handleItemStepChange={handleItemStepChange}
                  handleItemOrderToggle={handleItemOrderToggle}
                  handleItemCancel={handleItemCancel}
                  saveShippingEdit={saveShippingEdit}
                  openEdit={openEdit}
                  askDelete={askDelete}
                  openHistory={openHistory}
                />
              )
        }
        {/* 페이지 번호 — 계약관리와 같은 공용 부품(한 페이지뿐이면 그리지 않는다) */}
        {!loadingOrders && visibleOrders.length > 0 && (
          <Pagination page={promoPage} totalPages={promoTotalPages} onMove={setPromoPage} />
        )}
      </div>

      {/* 홍보물 금액 관리 모달 */}
      <ItemManageModal
        open={itemManageOpen}
        onClose={() => setItemManageOpen(false)}
        onSaved={() => { fetchActiveItems(); }}
      />

      {/* 주문 등록/수정 모달 */}
      <OrderModal
        open={orderModalOpen}
        initialData={editTarget}
        promotionalItemsList={promotionalItemsList}
        categoriesList={categoriesList}
        onClose={() => setOrderModalOpen(false)}
        // 신규 등록이면 1페이지로(새 주문이 보이게). 수정은 보던 페이지에 머문다.
        onSaved={() => { if (!editTarget) setPromoPage(1); fetchOrders(); }}
        pickCompanies={api.pickCompany ? screenMeta?.codes : null}
      />

      {/* 삭제 확인 모달 */}
      {deleteConfirm && (
        <div style={overlayStyle} {...overlayCloseProps}>
          <div style={confirmBoxStyle} onClick={(e) => e.stopPropagation()}>
            <div style={confirmHeaderStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div style={modalHeaderIconBox}>🗑️</div>
                <span style={modalHeaderTitleStyle}>삭제 확인</span>
              </div>
              <button style={closeBtnStyle} onClick={() => { setDeleteConfirm(false); setDeleteTarget(null); }}>✕</button>
            </div>
            <div style={confirmBodyStyle}>
              <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 14 }}>
                {deleteTarget?.company_name || '이 주문'}을(를) 삭제하시겠습니까?
              </p>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>삭제 후 복구할 수 없습니다.</p>
            </div>
            <div style={confirmFooterStyle}>
              <button style={cancelBtnStyle} onClick={() => { setDeleteConfirm(false); setDeleteTarget(null); }}>취소</button>
              <button style={deleteConfirmBtnStyle} disabled={deleting} onClick={handleDelete}>
                {deleting ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이력 모달 */}
      {historyOrder && (
        <HistoryModal
          order={historyOrder}
          data={historyData}
          loading={historyLoading}
          onClose={() => setHistoryOrder(null)}
        />
      )}
    </div>
    </PromoScreenContext.Provider>
  );
}
